import { cn } from "@/lib/utils"

import { SKETCH_CAP, SKETCH_VIEWBOX } from "./paths"
import { SKETCH_BORDER_SYMBOL_ID } from "./sprite"

type Window = { x: number; y: number; w: number; h: number }

const { width: W, height: H } = SKETCH_VIEWBOX
const CAP = SKETCH_CAP
const MID_W = W - CAP * 2
const MID_H = H - CAP * 2

// 9-slice windows into the shared path; the centre cell is empty (no border there).
// Grid order is row-major so it maps 1:1 onto CSS grid auto-placement.
const CELLS: ReadonlyArray<Window | null> = [
  { x: 0, y: 0, w: CAP, h: CAP },
  { x: CAP, y: 0, w: MID_W, h: CAP },
  { x: W - CAP, y: 0, w: CAP, h: CAP },
  { x: 0, y: CAP, w: CAP, h: MID_H },
  null,
  { x: W - CAP, y: CAP, w: CAP, h: MID_H },
  { x: 0, y: H - CAP, w: CAP, h: CAP },
  { x: CAP, y: H - CAP, w: MID_W, h: CAP },
  { x: W - CAP, y: H - CAP, w: CAP, h: CAP },
]

type SketchBorderProps = {
  className?: string
}

// Corners sit in square cells sized by --sketch-cap, so they scale uniformly.
// Edges live in 1fr cells and stretch along one axis only, keeping stroke thickness constant.
// The outline is ~1.3 units thick and straddles pixel rows; --sketch-stroke-boost (set by the host
// on low-DPR screens only) strokes the outline in CSS px so the line reads solid at 1x.
export function SketchBorder({ className }: SketchBorderProps) {
  return (
    <span
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 grid",
        "grid-cols-[var(--sketch-cap)_1fr_var(--sketch-cap)] grid-rows-[var(--sketch-cap)_1fr_var(--sketch-cap)]",
        className,
      )}
    >
      {CELLS.map((cell, index) =>
        cell ? (
          <svg
            key={index}
            viewBox={`${cell.x} ${cell.y} ${cell.w} ${cell.h}`}
            preserveAspectRatio="none"
            className="size-full overflow-hidden fill-current stroke-current [stroke-width:var(--sketch-stroke-boost,0)]"
          >
            <use href={`#${SKETCH_BORDER_SYMBOL_ID}`} />
          </svg>
        ) : (
          <span key={index} />
        ),
      )}
    </span>
  )
}
